import React, { useState, useEffect } from 'react';
import { DataGrid, GridToolbar } from '@mui/x-data-grid';
import {
  Autocomplete,
  Grid,
  Stack,
  Button,
  Container,
  Typography,
  IconButton,
  Box,
  Modal,
  TextField,
} from '@mui/material';
import { Helmet } from 'react-helmet-async';
import { Edit, Trash, Clock, Calendar, Download, Eye } from 'react-feather';
import { useNavigate } from 'react-router-dom';
import { saveAs } from 'file-saver';
import * as Yup from 'yup';
import { useFormik } from 'formik';
import CreateRouteForm from './CreateRouteForm';
import { loadLocations } from '../locations/Locations';
import Iconify from '../components/iconify';
import api from '../network/ApiClient';
import { useMerchantFilter, MerchantFilterSelect, ALL_MERCHANTS } from '../components/merchant-filter';

const style = {
  position: 'absolute',
  top: '50%',
  left: '50%',
  transform: 'translate(-50%, -50%)',
  width: {
    md: 500,
    xs: '100%',
  },
  bgcolor: 'background.paper',
  boxShadow: 24,
  maxHeight: '90vh',
  overflowY: 'auto',
  p: {
    md: 4,
    xs: 2,
  },
};

export default function RoutesPage() {
  const navigate = useNavigate();
  const [modalNewEventGroup, setModalNewEventGroupModalShow] = useState(false);
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [editRoute, setEditRoute] = useState(null);
  const [routes, setRoutes] = useState([]);
  const [routesError, setRoutesError] = useState(false);
  const [locations, setLocations] = useState({ data: [] });
  const [reportLoading, setReportLoading] = useState(false);
  const merchantFilter = useMerchantFilter(routes);

  // Rides report — POST /report/ridesales, mirroring the event reports'
  // download / view-in-excel pair (see RIDES_API.md). No route param = all the
  // caller's routes; backend defaults the date window. When a merchant is
  // selected in the filter, narrow the report to it (`merchant` narrows within
  // the caller's scope — RIDES_API.md); "All" omits the param.
  const fetchRidesReport = async (action) => {
    setReportLoading(true);
    try {
      const payload = { mode: action === 'download' ? 'file' : 'view' };
      if (merchantFilter.selected !== ALL_MERCHANTS) payload.merchant = merchantFilter.selected;
      const axiosConfig = { headers: { 'Content-Type': 'application/json' } };
      if (action === 'download') {
        axiosConfig.responseType = 'arraybuffer';
      }
      const response = await api.post('report/ridesales', payload, axiosConfig);
      if (action === 'download') {
        const blob = new Blob([response.data], {
          type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
        });
        saveAs(blob, 'Rides_Report.xlsx');
      } else {
        const fileUrl = response?.data?.url || response?.data?.link || response?.data?.fileUrl || response?.data;
        if (typeof fileUrl === 'string' && fileUrl.startsWith('http')) {
          const viewerUrl = `https://view.officeapps.live.com/op/view.aspx?src=${encodeURIComponent(fileUrl)}`;
          window.open(viewerUrl, '_blank', 'noopener,noreferrer');
        } else {
          window.alert('No preview URL returned for the rides report.');
        }
      }
    } catch (error) {
      console.error('Error fetching rides report:', error);
      window.alert('Unable to generate the rides report.');
    } finally {
      setReportLoading(false);
    }
  };

  const fetchRoutes = async () => {
    try {
      const res = await api.get(`${process.env.REACT_APP_API_URL}/route`);
      if (res.data && res.data.length > 0) {
        const routesWithIds = res.data.map((route, index) => ({
          ...route,
          id: route.id || index,
        }));
        setRoutes(routesWithIds);
        setRoutesError(false);
      } else {
        setRoutes([]);
        setRoutesError(true);
      }
    } catch (error) {
      setRoutesError(true);
    }
  };

  const deleteRoute = async (id) => {
    try {
      const cfs = window.confirm('Do you want to Delete this Route?');
      if (!cfs) {
        return;
      }
      await api.delete(`/route/${id}/delete`);
      setRoutes(routes.filter((route) => route.id !== id));
    } catch (error) {
      console.error('Error deleting route:', error);
    }
  };

  const handleEditRoute = (route) => {
    setEditRoute(route);
    setEditModalOpen(true);
  };

  const columns = [
    {
      field: 'name',
      headerName: 'Name',
      flex: 1,
    },
    {
      field: 'action',
      headerName: 'Actions',
      flex: 1,
      sortable: false,
      renderCell: (cellValues) => (
        <Stack direction="row" spacing={1}>
          <IconButton color="secondary" size="small" onClick={() => navigate(`/rides/${cellValues.row.id}`)}>
            <Clock size={15} />
          </IconButton>
          <IconButton color="primary" size="small" onClick={() => navigate(`/routes/${cellValues.row.id}`)}>
            <Edit size={15} />
          </IconButton>

          <IconButton color="error" size="small" onClick={() => deleteRoute(cellValues.row.id)}>
            <Trash size={15} />
          </IconButton>
        </Stack>
      ),
    },
  ];

  useEffect(() => {
    fetchRoutes();
    const fetchLocations = async () => {
      await loadLocations(setLocations);
    };
    fetchLocations();
  }, []);

  const handleCreateRoute = (newRoute) => {
    setRoutes([...routes, newRoute]);
  };

  const handleCloseEditModal = () => {
    setEditModalOpen(false);
    setEditRoute(null);
  };

  // Initialize formik with default values from editRoute
  const formik = useFormik({
    initialValues: {
      name: '',
      legs: [{ location: '', access: '2' }],
    },
    validationSchema: Yup.object({
      name: Yup.string().required('Route name is required'),
      legs: Yup.array().of(
        Yup.object({
          location: Yup.string().required('Please select a location'),
          access: Yup.string().required('Access is required'),
        })
      ),
    }),
    onSubmit: async (values, { setSubmitting }) => {
      setSubmitting(false);
      handleCloseEditModal();
    },
  });

  useEffect(() => {
    if (editRoute) {
      formik.setValues({
        name: editRoute.name,
        legs: [{ location: editRoute.legs[0]?.location || '', access: '2' }],
      });
    }
  }, [editRoute]);

  return (
    <>
      <Helmet>
        <title>Routes</title>
      </Helmet>
      <Container>
        <Typography variant="h4" mb={3}>
          Routes
        </Typography>

        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
          spacing={2}
          flexWrap="wrap"
          useFlexGap
          mb={5}
        >
          {/* Left: primary actions */}
          <Stack direction="row" alignItems="center" spacing={2}>
            <Button
              variant="contained"
              onClick={() => {
                setModalNewEventGroupModalShow(true);
              }}
              startIcon={<Iconify icon="eva:plus-fill" />}
            >
              Create Route
            </Button>
            <Button variant="contained" onClick={() => navigate(`/routes/calendar`)} startIcon={<Calendar size={18} />}>
              View Calendar
            </Button>
          </Stack>

          {/* Right: merchant filter (if shown) then the rides report */}
          <Stack direction="row" alignItems="center" spacing={2}>
            <MerchantFilterSelect
              merchants={merchantFilter.merchants}
              value={merchantFilter.selected}
              onChange={merchantFilter.setSelected}
              showFilter={merchantFilter.showFilter}
            />
            <Stack
              direction="row"
              alignItems="center"
              spacing={1.5}
              sx={{
                px: 2,
                py: 1,
                border: '1px solid',
                borderColor: 'divider',
                borderRadius: 1,
              }}
            >
              <Typography variant="body2" sx={{ mr: 0.5 }}>
                Rides Report:
              </Typography>
              <IconButton
                onClick={() => fetchRidesReport('download')}
                color="primary"
                size="small"
                title="Download XLSX"
                disabled={reportLoading}
              >
                <Download size={18} />
              </IconButton>
              <IconButton
                onClick={() => fetchRidesReport('preview')}
                color="secondary"
                size="small"
                title="View Excel"
                disabled={reportLoading}
              >
                <Eye size={18} style={{ color: 'green' }} />
              </IconButton>
            </Stack>
          </Stack>
        </Stack>

        <CreateRouteForm
          onCreateCallback={handleCreateRoute}
          modalShow={modalNewEventGroup}
          setModalShow={setModalNewEventGroupModalShow}
          onSubmitSuccess={fetchRoutes}
        />

        <Box sx={{ width: '100%', height: 400 }}>
          <DataGrid
            rows={merchantFilter.applyFilter(routes)}
            columns={columns}
            autoHeight
            pageSize={5}
            rowsPerPageOptions={[5]}
            disableSelectionOnClick
            disableColumnMenu
            disableDensitySelector
            sx={{
              '& .MuiDataGrid-cell:focus': {
                outline: 'none',
              },
              '& .MuiDataGrid-row:hover': {
                cursor: 'pointer',
              },
            }}
            componentsProps={{
              toolbar: {
                showQuickFilter: true,
              },
            }}
          />
        </Box>

        <Modal
          open={editModalOpen}
          onClose={handleCloseEditModal}
          aria-labelledby="edit-route-modal"
          aria-describedby="edit-route-form"
        >
          <Box sx={style}>
            <form onSubmit={formik.handleSubmit}>
              <Stack spacing={3}>
                <h4 className="card-title mb-0">Edit Route</h4>

                <TextField
                  fullWidth
                  name="name"
                  label="Route Name"
                  onChange={formik.handleChange}
                  value={formik.values.name}
                  variant="outlined"
                  error={formik.touched.name && Boolean(formik.errors.name)}
                  helperText={formik.touched.name && formik.errors.name}
                  InputLabelProps={{ shrink: true }}
                />

                <Autocomplete
                  options={locations.data}
                  getOptionLabel={(loc) => loc.name}
                  value={locations.data.find((loc) => loc.id === formik.values.legs[0]?.location) || null}
                  onChange={(event, newValue) => {
                    formik.setFieldValue('legs', [{ ...formik.values.legs[0], location: newValue?.id || '' }]);
                  }}
                  renderInput={(params) => <TextField {...params} label="Location" variant="outlined" fullWidth />}
                  isOptionEqualToValue={(option, value) => option.id === value.id}
                  freeSolo
                />
              </Stack>

              <Grid container sx={{ mt: 2 }} justifyContent="space-between">
                <Grid item xs={6}>
                  <Button variant="contained" type="submit" disabled={formik.isSubmitting}>
                    Submit
                  </Button>
                </Grid>
                <Grid item xs={6} textAlign="right">
                  <Button color="error" onClick={handleCloseEditModal}>
                    Cancel
                  </Button>
                </Grid>
              </Grid>
            </form>
          </Box>
        </Modal>
      </Container>
    </>
  );
}
